/* global globalThis */
// All workspace operations use the authenticated native service.
import { applyCustomNames } from './customNames.mjs';
import { signedSsoEntry } from './sso.mjs';
import { priorDecisionPeriods } from './decisionHistory.mjs';
import { workspaceEndpoint } from './serviceRouting.mjs';
function failure(message, code) { return Object.assign(new Error(message), { code }); }
export async function request(path, { method = 'GET', body, params, signal } = {}) {
  const query = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null)) : '';
  const endpoint = workspaceEndpoint(path, method);
  const response = await fetch(endpoint + query, { method, credentials: 'same-origin', signal,
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (response.status === 401 && path !== 'login') throw failure('登录已过期，请重新登录或选择课程。', 'AUTH_REQUIRED');
  const isJson = (response.headers.get('content-type') || '').includes('application/json');
  if (!isJson) throw failure('业务服务尚未连接，请检查业务 API 代理。', 'SERVICE_UNAVAILABLE');
  let data = await response.json();
  // Some legacy validation errors are serialized JSON strings.
  if (typeof data === 'string') { try { data = JSON.parse(data); } catch { /* keep legacy message */ } }
  if (!response.ok || data?.isSuccess === false) throw Object.assign(failure(data?.message || data?.error || (typeof data === 'string' ? data : `业务请求失败（${response.status}）`), data?.code || 'REQUEST_FAILED'),{current:data?.current,status:response.status});
  if (endpoint.startsWith('/api-proxy/service/workspace/')) {
    if (data?.isSuccess !== true || !Object.prototype.hasOwnProperty.call(data, 'result')) throw failure('工作区服务响应格式不正确。', 'SERVICE_UNAVAILABLE');
    return data.result;
  }
  return data;
}
export const loginStudent = body => request('login', { method: 'POST', body });
export const listSeminars = () => request('student/seminar');
export const chooseSeminar = seminarId => request('choose_seminar', { method: 'POST', body: { seminarId: String(seminarId) } });
export async function loginSignedSSO(input) {
  const entry = signedSsoEntry(input);
  await request('sso', { method: 'POST', body: Object.fromEntries(entry.params), signal: AbortSignal.timeout(15000) });
  const user = await request('user', { signal: AbortSignal.timeout(15000) });
  const actual = contextOf(user);
  if (String(actual.seminarId) !== entry.context.seminarId || actual.companyId == null) {
    throw new Error('尚未确认链接指定的课程和公司，请检查课程分配后重新登录。');
  }
  return { context:entry.context,destination:entry.destination };
}
export async function loginSSO(params) {
  const body = { partner: params.partner || 'glp', landingPage: 2, seminarId: params.seminarId,
    mobile: params.mobile, from: params.from || 'glp', team_id: params.team_id || params.teamId || params.userId, lang: params.lang || 'zh_CN', ai: params.ai === true || params.ai === 1 || ['true','1'].includes(params.ai) };
  if (!body.seminarId || !body.team_id || !body.mobile) throw new Error('请填写课程编号、团队 ID 和手机号。');
  const response = await fetch('/api-proxy/service/marksimos_token/user/', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!response.ok) throw new Error('SSO 服务连接失败，请检查本地 13003 服务或代理配置。');
  const result = await response.json();
  if (!result.isSuccess || !result.url) throw new Error(result.message || '无法取得游戏登录地址。');
  const url = new URL(result.url);
  if (!['http:', 'https:'].includes(url.protocol) || url.pathname !== '/sso') throw new Error('游戏登录地址格式不正确。');
  await loginSignedSSO(url.search);
}
export function contextOf(user) {
  const s = user?.currentMarksimosSeminar;
  return { seminarId: s?.seminarId, companyId: s?.currentCompany?.companyId, period: s?.currentPeriod };
}
export function isDecisionSubmitted(user) {
  const s=user?.currentMarksimosSeminar;
  if(typeof s?.decisionSubmitted==='boolean')return s.decisionSubmitted;
  const round=s?.roundTime?.find(row=>Number(row.period)===Number(s.currentPeriod))||s?.roundTime?.[s.currentPeriod-1];
  const company=round?.lockDecisionTime?.find(row=>Number(row.companyId)===Number(s.currentCompany?.companyId))||round?.lockDecisionTime?.[s.currentCompany?.companyId-1];
  return !!company?.lockStatus;
}
export function isDecisionLocked(user) {
  const s=user?.currentMarksimosSeminar;
  if(typeof s?.decisionLocked==='boolean')return s.decisionLocked;
  return !!s?.isSimulationFinished || Number(s?.actualPeriod)>Number(s?.simulationSpan);
}
export async function verifyContext(expected, { allowLocked = false } = {}) {
  const user = await request('user');
  const actual = contextOf(user);
  if (!expected || ['seminarId', 'companyId', 'period'].some(k => actual[k] == null || String(actual[k]) !== String(expected[k]))) {
    throw failure('课程、公司或阶段已变化，请刷新后重新核对决策。', 'CONTEXT_CHANGED');
  }
  if (!allowLocked && isDecisionLocked(user)) throw failure('本轮已锁定或课程已结束，请刷新查看最新状态。', 'DECISION_LOCKED');
  return user;
}
export const readCompany = context => request('company', { params: { ...context, decisionPeriod: context.period } });
export const readDecisionConstraints = (context, skuId, values, signal) => request('decision/constraints', {method:'POST',params:context,body:{skuId:Number(skuId),values},signal});
export async function readDecisionSupport(context, kind, skuId, { signal, customs = [] } = {}) {
  if (!context?.seminarId || context.companyId == null || context.period == null) throw new Error('请先连接课程');
  if (!['portfolio','forecast'].includes(kind) || (kind === 'forecast' && skuId == null)) throw new Error('请选择辅助数据和产品');
  const path = kind === 'portfolio' ? 'product_portfolio' : `future_projection_calculator/${encodeURIComponent(skuId)}`;
  const result = await request(path, { params: context, signal });
  const object = value => value && typeof value === 'object' && !Array.isArray(value);
  if (kind === 'portfolio' ? !Array.isArray(result) : !object(result?.currentPeriodInfo) || !object(result?.previousPeriodInfo) || !object(result?.expectedSales)) throw new Error('辅助决策数据格式不正确，请刷新重试');
  return applyCustomNames(result, customs);
}
export async function readProfitabilityReport(context, period, signal) {
  const params = { ...context, decisionPeriod: context.period };
  return request(`report/profitability_evolution_${period}`, { params, signal });
}
export async function readHistoricalComparison(context,{signal}={}) {
  const deadline=AbortSignal.timeout(20000);
  const result=await request('historical-comparison',{params:{seminarId:context.seminarId},signal:signal?AbortSignal.any([signal,deadline]):deadline});
  if(!result||typeof result!=='object'||!['available','not_finished','current_unavailable'].includes(result.status))throw new Error('历史比较数据格式不正确，请重新读取。');
  return result;
}
export function readDebriefNotes(scope,{signal}={}){
  return request('debrief-notes',{params:{seminarId:scope.seminarId,companyId:scope.companyId},signal:signal?AbortSignal.any([signal,AbortSignal.timeout(15000)]):AbortSignal.timeout(15000)});
}
export function saveDebriefNotes(scope,changes,{signal}={}){
  return request('debrief-notes',{method:'PATCH',params:{seminarId:scope.seminarId,companyId:scope.companyId},body:{changes},signal:signal?AbortSignal.any([signal,AbortSignal.timeout(15000)]):AbortSignal.timeout(15000)});
}
export async function loadWorkspace(signal) {
  const user = await request('user', { signal });
  const context = contextOf(user);
  if (!context.seminarId || !context.companyId) return { user, needsCourse: true, warnings: [] };
  // Start alongside the first company read, before the awards flow is mounted.
  // Keep the resolved result on this workspace snapshot; navigation never refetches it.
  const comparison = user.currentMarksimosSeminar?.isSimulationFinished
    ? readHistoricalComparison(context,{signal}).catch(()=>({status:'error'}))
    : Promise.resolve({status:'not_finished'});
  const params = { ...context, decisionPeriod: context.period };
  const company = await request('company', { params, signal });
  const completedPeriod = user.currentMarksimosSeminar?.isSimulationFinished || Number(user.currentMarksimosSeminar?.actualPeriod) > Number(context.period) ? Number(context.period) : Math.max(0, Number(context.period) - 1);
  const names = ['customs', 'info', 'spending', 'reports', 'scores', 'financialReports', 'inventoryReport', 'profitabilityReports'];
  const results = await Promise.allSettled([
    request(`getCustom/${encodeURIComponent(context.seminarId)}`, { signal }),
    request('company/otherinfo', { params, signal }), request('spending_details', { params, signal }),
    request(`report/company_status_${completedPeriod}`, { params, signal }), request('finalscore', { params, signal }), request('report/financial_report', { params, signal }),
    request('chart/inventoryReport', { params, signal }),
    readProfitabilityReport(context, completedPeriod, signal),
  ]);
  const customs = results[0].status === 'fulfilled' && Array.isArray(results[0].value) ? results[0].value : [];
  const data = { user, company: applyCustomNames(company, customs), customs, warnings: [] };
  results.forEach((result, i) => {
    if (i === 0) return; // Custom labels are optional; original names remain usable.
    if (result.status === 'fulfilled') data[names[i]] = applyCustomNames(result.value, customs);
    else data.warnings.push(`${names[i]}：${result.reason.message}`);
  });
  const periods = priorDecisionPeriods(context.period);
  const history = await Promise.allSettled(periods.map(period => request('company', { params: { ...context, decisionPeriod: period }, signal })));
  data.decisionHistory = history.map((result, i) => ({
    period: periods[i],
    products: result.status === 'fulfilled' ? applyCustomNames(result.value, customs) : null,
  }));
  data.historicalComparison = await comparison;
  // A cancelled course load must not publish its old snapshot after a new login/load.
  signal?.throwIfAborted();
  return data;
}
export const operationId=()=>globalThis.crypto.randomUUID();
export async function saveDecisionField(change,{signal}={}) {
  const {seminarId,companyId,period,entity,field,value,expectedVersion,operationId:id}=change;
  const deadline=AbortSignal.timeout(15000);
  return request('decision/field',{method:'PATCH',body:{seminarId:String(seminarId),companyId:Number(companyId),period:Number(period),entity,field,value,expectedVersion,operationId:id},signal:signal?AbortSignal.any([signal,deadline]):deadline});
}
export const readDecisionState=(context,{signal}={})=>request('decision/state',{params:context,signal});
export const socketTicket=({signal}={})=>request('socket-ticket',{method:'POST',body:{},signal});
export async function requestAdditionalBudget(context,value,{expectedVersion,operationId:id}){
  if(typeof value!=='number'||!Number.isFinite(value)||value<=0||Number(value.toFixed(2))!==value)throw new Error('申请金额须为正数，最多两位小数');
  await verifyContext(context);
  const entity={type:'company',id:Number(context.companyId)},field='d_RequestedAdditionalBudget';
  return saveDecisionField({...context,seminarId:String(context.seminarId),entity,field,value,expectedVersion,operationId:id});
}
export async function saveSku(body){
  await verifyContext(body);
  for(const [field,value] of Object.entries(body.sku_data))await saveDecisionField({...body,entity:{type:'sku',id:Number(body.sku_id)},field,value:field==='d_FactoryPrice'?value[0]:value,expectedVersion:body.fieldVersions?.[`sku:${body.sku_id}:${field}`]||0,operationId:operationId()});
}
export async function createSku(context,brandId,suffix,id=operationId()){
  if(!brandId||!/^[a-zA-Z0-9]$/.test(suffix))throw new Error('请选择品牌并输入一个英文字母或数字');
  await verifyContext(context);
  return request('decision/sku',{method:'POST',body:{...context,seminarId:String(context.seminarId),brandId:Number(brandId),suffix,operationId:id}});
}
export async function submitDecision(context,id=operationId()){
  const before=await verifyContext(context,{allowLocked:true});if(isDecisionLocked(before))throw failure('本轮已开始运行或课程已结束。','DECISION_LOCKED');
  await request('decision/submit',{method:'POST',body:{...context,seminarId:String(context.seminarId),operationId:id}});
  const after=await verifyContext(context,{allowLocked:true});if(!isDecisionSubmitted(after))throw new Error('暂未确认服务端提交状态，请刷新核对。');return after;
}
export async function deleteSku(context,skuId,expectedRevision,id=operationId()){
  if(!Number.isSafeInteger(skuId)||skuId<1||!Number.isSafeInteger(expectedRevision)||expectedRevision<0)throw new Error('请选择有效的产品');
  await verifyContext(context);
  return request('decision/sku',{method:'DELETE',body:{...context,seminarId:String(context.seminarId),skuId,expectedRevision,operationId:id},signal:AbortSignal.timeout(15000)});
}
export async function runRound(context,id=operationId()){
  const user=await verifyContext(context,{allowLocked:true}),s=user.currentMarksimosSeminar;
  if(!s.isSinglePlayer)throw new Error('团队课程由教师运行，请等待教师推进阶段。');
  if(s.isSimulationFinished||!isDecisionSubmitted(user))throw new Error('请先提交本轮决策，且课程必须尚未结束。');
  const result=await request('decision/run',{method:'POST',body:{...context,seminarId:String(context.seminarId),operationId:id}});
  if(result.status!=='completed')throw failure('运行结果尚未确认，请刷新课程状态。为避免重复运行，本页暂不重复发送。','RUN_UNKNOWN');return result;
}

export async function saveInvestment(body){
  await verifyContext(body);
  const entity=body.brand_id==null?{type:'company',id:Number(body.companyId)}:{type:'brand',id:Number(body.brand_id)};
  for(const [field,value] of Object.entries(body.company_data||body.brand_data))await saveDecisionField({...body,entity,field,value,expectedVersion:body.fieldVersions?.[`${entity.type}:${entity.id}:${field}`]||0,operationId:operationId()});
}
export async function createBrand(context,name,suffix,id=operationId()){
  if(!/^[a-zA-Z0-9]{1,5}$/.test(name)||!/^[a-zA-Z0-9]$/.test(suffix))throw new Error('请输入有效的品牌名称与首个产品编号');
  await verifyContext(context);
  return request('decision/brand',{method:'POST',body:{...context,seminarId:String(context.seminarId),name,suffix,operationId:id}});
}
